// ─────────────────────────────────────────────────────────────────────────────
// interaction_context.ts — Stylus tool modes and spatial interaction routing
//
// Defines what happens to pen input in any given spatial region.
// The "where gate": on pointerdown, the first contact point determines which
// InteractionContext governs the entire gesture — global page default, or a
// local InteractionBox override.  Once locked, no further spatial evaluation
// is needed until pen-up.
//
// StylusTool describes how ink behaves:
//   'pen'   — standard: strokes commit and persist
//   'wand'  — practice: strokes fade after pen-up; spells still fire normally
//   'none'  — read-only: no ink produced, touch events may scroll/navigate
//
// DispatchMode describes where resolved spell actions go:
//   'live'     — normal: actions flow to the action bus (host + local)
//   'diverted' — sandbox: actions are intercepted by a local handler
//                (used by Codex practice areas, tutorials, games)
// ─────────────────────────────────────────────────────────────────────────────

// ── Stylus tool ──────────────────────────────────────────────────────────────

export type StylusTool = 'pen' | 'wand' | 'none';

// ── Dispatch routing ─────────────────────────────────────────────────────────

export type DispatchMode = 'live' | 'diverted';

/** Callback for diverted spell casts — receives the classified spell info. */
export type DivertedCastHandler = (
  spellId: string,
  params: Record<string, unknown>,
  family: string,
  confidence: number,
) => void;

// ── Interaction context ──────────────────────────────────────────────────────

export interface InteractionContext {
  /** What the stylus produces. */
  tool: StylusTool;
  /** Whether the slip-knot initiator is active. */
  spellsAllowed: boolean;
  /** Where resolved actions go. */
  dispatchMode: DispatchMode;
  /** Handler called when dispatchMode is 'diverted' and a spell resolves. */
  onDivertedCast?: DivertedCastHandler;
}

// ── Preset contexts ──────────────────────────────────────────────────────────

/** Standard journal page — full editing, live dispatch. */
export const JOURNAL_CONTEXT: InteractionContext = {
  tool: 'pen',
  spellsAllowed: true,
  dispatchMode: 'live',
};

/** Codex practice area — wand ink (fades), spells diverted for recognition. */
export const CODEX_PRACTICE_CONTEXT: InteractionContext = {
  tool: 'wand',
  spellsAllowed: true,
  dispatchMode: 'diverted',
};

// ── Adventure contexts ───────────────────────────────────────────────────────

/** Adventure action page — full editing, real-time, spells live. */
export const ADVENTURE_ACTION_CONTEXT: InteractionContext = {
  tool: 'pen',
  spellsAllowed: true,
  dispatchMode: 'live',
};

/** Adventure inventory page — drawing = conjuring. */
export const ADVENTURE_INVENTORY_CONTEXT: InteractionContext = {
  tool: 'pen',
  spellsAllowed: true,
  dispatchMode: 'live',
};

/** Adventure read-only sub-tabs (character, history, build). */
export const ADVENTURE_READONLY_CONTEXT: InteractionContext = {
  tool: 'none',
  spellsAllowed: false,
  dispatchMode: 'live',
};

/** Adventure bookmarks — wand for glyph drawing, spells diverted for matching. */
export const ADVENTURE_BOOKMARK_CONTEXT: InteractionContext = {
  tool: 'wand',
  spellsAllowed: true,
  dispatchMode: 'diverted',
};

// ── Interaction box ──────────────────────────────────────────────────────────
// A spatial override region embedded in a page.  The page's global context
// applies everywhere except inside these boxes.

export interface InteractionBox {
  id: string;
  /** Bounding rectangle in page-local coordinates. */
  bbox: { x: number; y: number; width: number; height: number };
  /** The context that applies inside this box. */
  context: InteractionContext;
  /** Optional label shown at the box edge (e.g. "Practice here"). */
  label?: string;
}

// ── Page-level interaction policy ────────────────────────────────────────────
// Each page (or page-group kind) declares a default context and optional boxes.

export interface PageInteractionPolicy {
  /** Applies to all stylus input outside any InteractionBox. */
  defaultContext: InteractionContext;
  /** Regions with locally overridden context. */
  boxes: InteractionBox[];
}

// ── Where gate ───────────────────────────────────────────────────────────────
// Resolves the active InteractionContext for a pointerdown at (x, y).
// Called once per gesture.  Returns the locked context for the stroke duration.

export function resolveContext(
  policy: PageInteractionPolicy,
  x: number,
  y: number,
): { context: InteractionContext; boxId: string | null } {
  for (const box of policy.boxes) {
    const b = box.bbox;
    if (x >= b.x && x <= b.x + b.width && y >= b.y && y <= b.y + b.height) {
      return { context: box.context, boxId: box.id };
    }
  }
  return { context: policy.defaultContext, boxId: null };
}
